import { useState, type FormEvent } from 'react';
import { api, type TicketDetail, type TicketType } from '../api.ts';
import { message } from '../format.ts';
import { ImagePicker } from './ImagePicker.tsx';
import type { PreparedImage } from './prepareImage.ts';
import {
  BODY_MAX,
  SUBJECT_MAX,
  TYPE_OPTIONS,
  hasErrors,
  validateTicket,
  type TicketErrors,
} from './tickets.ts';

interface NewTicketProps {
  readonly onOpened: (ticket: TicketDetail) => void;
  readonly onCancel: () => void;
}

export const NewTicket = ({ onOpened, onCancel }: NewTicketProps) => {
  const [subject, setSubject] = useState('');
  const [type, setType] = useState<TicketType>('bug');
  const [body, setBody] = useState('');
  const [image, setImage] = useState<PreparedImage | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const errors: TicketErrors = attempted ? validateTicket({ subject, body }) : {};

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    setFailure(null);
    if (hasErrors(validateTicket({ subject, body }))) return;
    setSending(true);
    try {
      const { ticket } = await api.openSupportTicket({
        subject: subject.trim(),
        type,
        body,
        ...(image === null ? {} : { attachment: image.attachment }),
      });
      onOpened(ticket);
    } catch (cause) {
      // A 429 carries the server's own sentence about how many tickets are
      // open, which is more useful than anything this form could guess.
      setFailure(message(cause));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card sp-new">
      <div className="card-head">
        <h2>Open a ticket</h2>
      </div>
      <div className="card-body">
        <form onSubmit={(event) => void submit(event)} noValidate>
          <div className="field">
            <label htmlFor="sp-subject">Subject</label>
            <input
              id="sp-subject"
              value={subject}
              maxLength={SUBJECT_MAX}
              disabled={sending}
              aria-required="true"
              aria-invalid={errors.subject === undefined ? undefined : true}
              aria-describedby={errors.subject === undefined ? undefined : 'sp-subject-error'}
              onChange={(event) => setSubject(event.target.value)}
            />
            {errors.subject !== undefined && (
              <p className="sp-field-error" id="sp-subject-error">
                {errors.subject}
              </p>
            )}
          </div>

          <div className="field sp-type">
            <label htmlFor="sp-type">Type</label>
            <select
              id="sp-type"
              value={type}
              disabled={sending}
              onChange={(event) => setType(event.target.value as TicketType)}
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="sp-body">Message</label>
            <textarea
              id="sp-body"
              value={body}
              maxLength={BODY_MAX}
              disabled={sending}
              aria-required="true"
              aria-invalid={errors.body === undefined ? undefined : true}
              aria-describedby={
                errors.body === undefined ? 'sp-body-help' : 'sp-body-help sp-body-error'
              }
              onChange={(event) => setBody(event.target.value)}
            />
            <span className="hint" id="sp-body-help">
              What you did, what you expected and what happened instead. Everyone in your account
              can read this ticket.
            </span>
            {errors.body !== undefined && (
              <p className="sp-field-error" id="sp-body-error">
                {errors.body}
              </p>
            )}
          </div>

          <ImagePicker
            id="sp-new-image"
            value={image}
            onChange={setImage}
            disabled={sending}
            onBusy={setPreparing}
          />

          <div className="toolbar">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={sending || preparing}
              aria-busy={sending || undefined}
            >
              {sending ? 'Sending…' : 'Open ticket'}
            </button>
            <button type="button" className="btn btn-quiet" disabled={sending} onClick={onCancel}>
              Cancel
            </button>
            {failure !== null && (
              <p className="sp-field-error" role="alert">
                {failure}
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
